import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { IDashboardFilters } from "../interfaces/dashboard";
import { getLocalMarketplaceId } from "../utils/storage";
import {
  MERCHANT,
  INTERNAL,
  COUNTRY_ARG,
  isMacroClient,
} from "../utils/constants";

const DEFAULT_DAYS = 30;

// Formatea a YYYY-MM-DD usando la fecha local (no UTC): toISOString() convierte
// a UTC y en AR (UTC-3) corre el día cerca de medianoche.
const toLocalDateString = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultFrom = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - DEFAULT_DAYS);
  return toLocalDateString(d);
};

const getDefaultTo = (): string => toLocalDateString(new Date());

export const useDashboardFilters = (entity?: string) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get("from") || getDefaultFrom();
  const to = searchParams.get("to") || getDefaultTo();
  const carrier_id = searchParams.get("carrier_id") || undefined;
  const status = searchParams.get("status") || undefined;
  const province = searchParams.get("province") || undefined;
  const merchant_id = searchParams.get("merchant_id") || undefined;
  // Macro es mono-cliente: el drill-down por client_id no aplica (el scope resuelve
  // siempre al único cliente). No se envía aunque venga en la URL.
  const client_id = isMacroClient()
    ? undefined
    : searchParams.get("client_id") || undefined;
  // Solo el merchant hereda el marketplace seleccionado en el navbar (localStorage).
  // Para el resto —en particular el usuario interno, cross a todas las entidades—
  // el marketplaceId es un drill-down opcional que solo llega por la URL; heredar
  // un valor residual mostraría datos de un cliente arbitrario.
  const marketplaceId =
    searchParams.get("marketplaceId") ||
    (entity === MERCHANT ? getLocalMarketplaceId() : undefined) ||
    undefined;
  // País solo para el usuario interno: default "ARG" (no mezcla países; siempre
  // consulta uno). Para el resto de entidades el país lo implica su cliente, así
  // que no se envía. En Macro (mono-país, sin asignaciones de país) tampoco se
  // envía: mandar `country` filtraría por un valor inexistente y rompería la query.
  const country =
    entity === INTERNAL && !isMacroClient()
      ? searchParams.get("country") || COUNTRY_ARG
      : undefined;

  const filters: IDashboardFilters = {
    from,
    to,
    carrier_id,
    status,
    province,
    merchant_id,
    client_id,
    marketplaceId,
    country,
  };

  const applyFilters = useCallback(
    (newFilters: Partial<IDashboardFilters>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return { filters, applyFilters, clearFilters };
};
